/**
 * Admin → Tenant ↔ Rate Card junction (per-customer multi-card cascade).
 *
 * Per ADR-008, a customer can have N rate cards assigned in cascade
 * order: cards are tried in card_priority ASC at dispatch time, first
 * card with a matching rate wins. A tenant with one row behaves like
 * the single-card case automatically.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@/lib/api';
import type { RateCardStrategy } from './useAdminRateCards';

export interface TenantRateCardAssignment {
  tenant_id: number;
  rate_card_id: number;
  rate_card_name: string;
  strategy: RateCardStrategy;
  card_priority: number;
  effective_from: string;
  effective_to?: string;
  created_at: string;
}

export interface TenantRateCardsResponse {
  items: TenantRateCardAssignment[];
}

export interface AssignTenantRateCardInput {
  rate_card_id: number;
  card_priority?: number;
  effective_from?: string;
  effective_to?: string;
}

export interface TestPrefixInput {
  destination: string;
  message_type?: string;
}

export interface TestPrefixResult {
  found: boolean;
  rate_card_id?: number;
  rate_card_name?: string;
  strategy?: RateCardStrategy;
  provider_name?: string;
  prefix?: string;
  buy_cost?: number;
  sell_price?: number;
}

const tenantCardsKey = (tenantId: number) =>
  ['admin', 'customers', tenantId, 'rate-cards'] as const;

export function useTenantRateCards(tenantId: number | null) {
  return useQuery({
    queryKey: tenantId ? tenantCardsKey(tenantId) : ['admin', 'customers', 'noop', 'rate-cards'],
    queryFn: () =>
      api.get<TenantRateCardsResponse>(`/v1/admin/customers/${tenantId}/rate-cards`),
    enabled: tenantId !== null && tenantId > 0,
    staleTime: 30_000,
  });
}

export function useAssignTenantRateCard(tenantId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: AssignTenantRateCardInput) =>
      api.post<TenantRateCardAssignment>(
        `/v1/admin/customers/${tenantId}/rate-cards`,
        input,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: tenantCardsKey(tenantId) });
    },
  });
}

export function useUnassignTenantRateCard(tenantId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (cardId: number) =>
      api.delete<void>(`/v1/admin/customers/${tenantId}/rate-cards/${cardId}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: tenantCardsKey(tenantId) });
    },
  });
}

export function useReorderTenantRateCard(tenantId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ cardId, cardPriority }: { cardId: number; cardPriority: number }) =>
      api.patch<void>(
        `/v1/admin/customers/${tenantId}/rate-cards/${cardId}`,
        { card_priority: cardPriority },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: tenantCardsKey(tenantId) });
    },
  });
}

/** Simulator: returns what the dispatcher would resolve for a destination. */
export function useTestPrefix(tenantId: number) {
  return useMutation({
    mutationFn: (input: TestPrefixInput) =>
      api.post<TestPrefixResult>(
        `/v1/admin/customers/${tenantId}/rate-cards/test`,
        input,
      ),
  });
}
